const destinations = [
  {
    name: "Caribbean & Cruise Ports",
    context: "Nassau, St. Thomas, Cozumel, and Grand Cayman are the top cruise destinations for Delray Beach travelers departing from Port Everglades and Port of Miami. Holland America, Celebrity, and Royal Caribbean all sail regularly from these ports with passengers from Palm Beach County",
    urgency: "Repositioning cruises and last-minute deals often book 2–3 weeks out — with passport requirements that surprise first-time cruisers",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Europe & Mediterranean",
    context: "Rome, Barcelona, and the Greek Islands draw Delray Beach retirees on luxury cruises and extended land tours. Paris, London, and the Swiss Alps are popular for multi-generational family trips booked through luxury travel advisors",
    urgency: "European river cruises and villa rentals have fixed departure dates — a passport discovered expired in November can derail a Christmas market trip or January sailing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Israel & Jewish Heritage",
    context: "Delray Beach has one of South Florida's largest Jewish communities. Birthright trips, heritage tours to Jerusalem, and family visits to Tel Aviv generate significant passport demand. Many residents maintain dual connections and travel regularly",
    urgency: "Organized Birthright and heritage trips have group departure dates that cannot be adjusted for individual passport delays — emergency processing is often required",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Toronto, Montreal, and Vancouver attract Delray Beach business travelers and snowbirds visiting summer homes. Niagara Falls and Quebec City are popular for family road trips. PBI offers nonstop service to Toronto seasonally",
    urgency: "Summer travel to Canada is often booked in May — with passport validity checks that happen too late for standard processing timelines",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Latin America",
    context: "Costa Rica, Panama, and Colombia draw adventure travelers and wellness seekers from Delray Beach. Arenal volcano tours, Panama Canal cruises, and Medellin flower festivals are increasingly popular with the South Florida retiree set",
    urgency: "Adventure travel and wellness retreat bookings often confirm 10–14 days before departure — with no flexibility around fixed retreat start dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Bahamas & Yachting",
    context: "Nassau, the Exumas, and the Abacos are the top yachting destinations for Delray Beach boat owners and charter guests. The Bahamas is a weekend destination for experienced South Florida boaters — but requires a valid passport for every passenger",
    urgency: "Weekend Bahamas trips are planned in days, not weeks. A passport discovered expired on Wednesday can cancel a Friday departure — we have handled these on 24-hour timelines",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "64%", label: "of Delray Beach expedited requests involve cruise bookings, yacht travel, or multi-generational family trips with fixed departure dates" },
  { stat: "2–3 Weeks", label: "typical booking-to-departure window for Delray Beach luxury travelers — too short for standard processing" },
  { stat: "Nov–Jan", label: "peak snowbird arrival season when passport discoveries spike — many find expired documents while unpacking" },
  { stat: "24 Hours", label: "shortest yacht emergency we have accommodated for a Delray Beach client with a Bahamas crossing planned" },
];

export default function DelrayDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Delray Beach Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Delray Beach's travel profile is defined by luxury cruising, multi-generational family trips, yacht charters, and seasonal snowbird migration. Understanding these patterns helps us anticipate the compressed timelines and document issues that arise in this market.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across South Florida and all of Florida with the same expedited passport expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/boca-raton" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Boca Raton <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/west-palm-beach" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                West Palm Beach <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/miami" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Miami <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}